import { HttpClient } from '@angular/common/http';
import { Injector } from '@angular/core';
import { ePlatforms } from '@common/networks';
import { Route } from '@lifi/sdk';
import { BaseContractFactory } from '@services/currencies-factory/contract-factory-base';
import { EVMErc20ContractFactory } from '@services/currencies-factory/evm-erc20-contract-factory.service';
import { NftObserverService } from '@services/nft-observer/nft-observer.service';
import { ethers } from 'ethers';
import { Observable, ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';

import { ActionService } from '../action/action.service';
import { CryptoWalletApi } from '../api/crypto-wallet';
import { EncryptedWalletApi } from '../api/encrypted-wallet';
import { IToken } from '../api/token';
import { UserApi } from '../api/user';
import { AppKeyV2Service } from '../app-password/app-key-v2.service';
import { AppPasswordService } from '../app-password/app-password.service';
import { ConfigService, TConfig } from '../config/config.service';
import { CryptoWalletService } from '../crypto-wallet/crypto-wallet.service';
import { CryptoService } from '../crypto/crypto.service';
import { EventTransportService } from '../event-transport/event-transport.service';
import { LoggerService } from '../logger/logger.service';
import { NetworksService } from '../networks/networks.service';
import { ICurrency } from './base-currency';
import { IOracleFee, ITransactionResponceEx } from './explorers/types';

export interface IWallet
{
    platform: ePlatforms;
    address: string;
}

export interface IContract
{
    [ network: string ]: {
        address: string,
        abi: string;
    };
}

export type ContractLike = string | IContract;

export function getPlatformFromString(_platform: string): ePlatforms
{
    for (let key in ePlatforms)
    {
        if (key.toLowerCase() === _platform.toLowerCase()) return ePlatforms[ key ];
    }
}

export abstract class Wallet
{
    address: string;
    protected name: string;
    protected platform: ePlatforms;
    protected id: number;
    protected iconUrl: string;
    protected currenciesCount = 0;
    protected currencies: ICurrency[] = [];
    protected baseCoins: ICurrency[] = [];
    protected cryptoWallet: CryptoWalletService;
    protected cryptoWalletId: string;
    protected currenciesSubject: ReplaySubject<ICurrency[]> = new ReplaySubject(1);
    protected privateWallet: any;
    protected isWalletReady: ReplaySubject<boolean> = new ReplaySubject(1);
    protected network: number[];
    protected actionService: ActionService;
    protected provider: ethers.Provider | any;
    protected history: ITransactionResponceEx[] = [];
    protected encryptedWalletApi: EncryptedWalletApi;
    protected cryptoWalletApi: CryptoWalletApi;
    protected cryptoService: CryptoService;
    protected eventTransport: EventTransportService;
    protected oldPasswordService: AppPasswordService;
    protected passwordService: AppKeyV2Service;
    protected userApi: UserApi;
    protected networksService: NetworksService;
    protected loggerService: LoggerService;
    protected http: HttpClient;
    protected currenciesLoadedSubject: ReplaySubject<boolean> = new ReplaySubject(1);
    protected configService: ConfigService;
    protected initCurrencies = false;
    protected hd_path: string;
    protected accIndex: number;
    protected nftObserver: NftObserverService;
    protected hidden = false;
    protected orderIndex: number;
    protected balanceOff = false;
    protected collapsed = false;
    protected contractFactory: BaseContractFactory;
    protected signer: ethers.Signer;
    protected includeNftObserve: boolean;

    constructor(
        protected injector: Injector,
        _cryptoWallet: CryptoWalletService,
        _cryptoWalletId: string,
        _address: string,
        _initCurrencies: boolean,
        _platform: ePlatforms,
        _dbId: number,
        _hd_path: string | null,
        _accountIndex: number,
        name?: string,
        hidden?: boolean,
        orderIndex?: number,
        balanceOff?: boolean,
        collapsed?: boolean,
        includeNftObserve?: boolean
    )
    {
        this.address = _address;
        this.platform = _platform;
        this.name = name || _platform;
        this.id = _dbId;
        this.cryptoWallet = _cryptoWallet;
        this.cryptoWalletId = _cryptoWalletId;
        this.initCurrencies = _initCurrencies;
        this.actionService = this.injector.get(ActionService);
        this.encryptedWalletApi = this.injector.get(EncryptedWalletApi);
        this.cryptoWalletApi = this.injector.get(CryptoWalletApi);
        this.cryptoService = this.injector.get(CryptoService);
        this.eventTransport = this.injector.get(EventTransportService);
        this.oldPasswordService = this.injector.get(AppPasswordService);
        this.passwordService = this.injector.get(AppKeyV2Service);
        this.networksService = this.injector.get(NetworksService);
        this.http = this.injector.get(HttpClient);
        this.configService = this.injector.get(ConfigService);
        this.userApi = this.injector.get(UserApi);
        this.loggerService = this.injector.get(LoggerService);
        this.hd_path = _hd_path;
        this.accIndex = _accountIndex;
        this.hidden = Boolean(hidden);
        this.orderIndex = orderIndex;
        this.balanceOff = Boolean(balanceOff);
        this.collapsed = Boolean(collapsed);
        this.includeNftObserve = Boolean(includeNftObserve);
        this.contractFactory = this.injector.get(EVMErc20ContractFactory);
    }

    protected abstract loadCurrencies(): any;
    protected abstract createCoinCurrencies(chainIdFilter: number[]): Promise<ICurrency[]>;
    public abstract getInstanceTokenByAddress(contract: any, _chainId: number): Promise<ICurrency>;
    public abstract getHistory(_chainId: number): Promise<ITransactionResponceEx[]>;
    public abstract getPrivateKey(): Promise<any>;
    public abstract checkContractAddress(_chainId: number, address: string): Promise<boolean>;
    public abstract getEncryptedJSON(_password: string): Promise<string>;
    public abstract setEncryptedData(_encrData: string, _password: string): Promise<any>;
    public abstract setPrivateKey(_privateKey: string): Promise<any>;
    public abstract setWalletPhrase(_phrase: string): void;
    public abstract signMsg(_message: string, chain_id: number, signingType: string, displayData?: string): Promise<any>;
    public abstract signTransactionBundle(txs: any[], chain_id: number): Promise<any>;
    public abstract sendTransaction(_txData: any, _txInfo: { symbol: string, value: string, to?: string, icon?: string; }, _chainId: number, feeRequired?: boolean): Promise<any>;
    public abstract sendGasLessTx(_txData: any, _txInfo: { symbol: string, value: string, paymaster: string, to?: string, icon?: string; }, _chainId: number, feeRequired?: boolean): Promise<any>;
    public abstract swap(_route: Route);
    public abstract sendTransactionOld(_txData: any, _txInfo: { symbol: string, value: string, to?: string, icon?: string; }, _chainId: number, feeRequired?: boolean): Promise<any>;
    public abstract addCurrencyByContract(_tokenAddress: string | null, _symbol: string, _dbCurrency: IToken, _id?: number, _config?: TConfig): void;
    public abstract removeCurrency(_symbol: string, _contractAddressUpper: string, chainId: number): boolean;
    public abstract removeCurrencyByID(currencyID: number): boolean;
    public abstract getFeeOracle(_chainId: number): Promise<IOracleFee>;
    public abstract convertFeeGwei(_feeAmount: string, _feeLimit: string, _feePrice: string, _convertToCoin: boolean): Promise<string>;
    public abstract convertFeeCoinToGwei(_customFee: string, _feeLimit: string): string;
    public abstract getFee(_feeLimit: string, _feeAmount: string): string;
    public abstract destroyWallet(): void;
    public abstract findNFTInStore(_chainId: number, _contractAddress: string, _tokenId: string);
    public abstract reloadNftData(_contractAddress: string, _chainId: number, _tokenId: string, _explorer?: string);
    public abstract updateNFTData();
    public abstract getNFTTotalData();
    public abstract getNewNFTData();
    public abstract handleNFTCollapse();
    public abstract getDataNFTToken(_accountNft, _chainId: number);
    public abstract populateNFTTransaction(_contractAddress, _chainId, _data, _nftType: 'ERC-721' | 'ERC-1155', _isGetFeeLimitNFT?: boolean);
    public abstract sendNFTTransaction(
        _tx: ethers.TransactionRequest,
        _txInfo: { symbol: string, value: string, to?: string; },
        _chainId: number,
        _feeRequired?: boolean
    ): Promise<any>;
    public abstract checkPrivateDataExist(): Promise<boolean>;
    public abstract getParseServices();
    public abstract getTransactionsCount(_chainId: number, address?: string): Promise<number>;;

    public getName(): string
    {
        return this.name;
    }

    public changeName(name): void
    {
        this.name = name;
    }

    public getAddress(): string
    {
        return this.address;
    }

    public getCryptoWalletId(): string
    {
        return this.cryptoWalletId;
    }

    public getIconUrl(): string
    {
        return this.iconUrl;
    }

    public getId(): number
    {
        return this.id;
    }

    public getPlatform(): string
    {
        return this.platform;
    }

    get platformWallet(): string
    {
        return this.platform;
    }

    public onWalletReady(): Observable<boolean>
    {
        return this.isWalletReady.asObservable();
    }

    public getCurrencies(): Observable<ICurrency[]>
    {
        return this.currenciesSubject.asObservable();
    }

    public isAllCurrenciesLoaded(): Observable<boolean>
    {
        return this.currenciesLoadedSubject.asObservable();
    }

    public getHidden(): boolean
    {
        return this.hidden;
    }

    public toggleHidden(value?): void
    {
        this.hidden = value !== undefined ? value : !this.hidden;
    }

    public getBalanceStatus(): boolean
    {
        return this.balanceOff;
    }

    public toggleBalanceStatus(value?): void
    {
        this.balanceOff = value !== undefined ? value : !this.balanceOff;
    }

    public getCollapsed()
    {
        return this.collapsed;
    }

    public toggleCollapsed(value?)
    {
        this.collapsed = value !== undefined ? value : !this.collapsed;
    }

    public getPath(): string | null
    {
        if (!this.hd_path)
        {
            return null;
        }
        return this.hd_path.replace('{account_index}', String(this.accIndex));
    }

    public getOrderIndex(): number
    {
        return this.orderIndex;
    }

    public setOrderIndex(index: number): void
    {
        this.orderIndex = index;
    }

    public async getCurrencyByAddress(_address: string): Promise<ICurrency>
    {
        await this.isAllCurrenciesLoaded().pipe(take(1)).toPromise();
        return this.currencies.find(
            (_currency) => _currency.getAddress() === _address
        );
    }

    public async getCurrencyBySymbol(symbol: string, chainId: number): Promise<ICurrency>
    {
        await this.isAllCurrenciesLoaded().pipe(take(1)).toPromise();

        return this.currencies.find(
            (currency) => currency.getSymbol() === symbol && currency.getChainId() === +chainId
        );
    }

    public async getCurrencyByTokenId(tokenId: number, chainId: number): Promise<ICurrency>
    {
        await this.isAllCurrenciesLoaded().pipe(take(1)).toPromise();

        return this.currencies.find(
            (currency) => currency.getCurrencyId() === +tokenId && currency.getChainId() === +chainId
        );
    }

    public async getCurrencyByChainId(_chainId: number, _isAllCurrencies = false): Promise<ICurrency | ICurrency[]>
    {
        await this.isAllCurrenciesLoaded().pipe(take(1)).toPromise();

        if (_isAllCurrencies)
        {
            return this.currencies.filter(
                (_currency) => (_currency.getChainId() === +_chainId)
            );
        }
        else
        {
            return this.currencies.find(
                (_currency) => (_currency.getChainId() === +_chainId)
            );
        }

    }

    public async getBaseCoinByChainId(_chainId: number): Promise<ICurrency>
    {
        await this.isAllCurrenciesLoaded().pipe(take(1)).toPromise();

        let coin = this.baseCoins.find(
            (_currency) => (_currency.getChainId() === +_chainId)
        );

        if (coin == undefined)
        {
            coin = (await this.createCoinCurrencies([ _chainId ]))[ 0 ];
        }

        return coin;
    }

    public async getFirstBaseCoin(): Promise<ICurrency>
    {
        await this.isAllCurrenciesLoaded().pipe(take(1)).toPromise();

        if (this.baseCoins.length > 0)
        {
            return this.baseCoins[ 0 ];
        }
        return null;
    }

    protected checkCurrenciesLoaded(): void
    {
        if (this.currenciesCount === this.currencies.length)
        {
            this.currenciesSubject.next(this.currencies);
            this.currenciesLoadedSubject.next(true);
        }
    }

    public static getWalletDataByEncryptedJson(
        _json: string,
        _password: string
    ): string
    {
        return null;
    }

    public getWalletBlockchains(wallet: Wallet)
    {
        const walletBlockchains = [];

        if (wallet.currencies.length > 0)
        {
            const walletChainIdAll = [];

            wallet.currencies.forEach((currency: any) =>
            {
                if (!walletChainIdAll.includes(currency.chainId))
                {
                    walletChainIdAll.push(currency.chainId);
                }
                return walletChainIdAll;
            });

            walletChainIdAll
                .sort((a, b) => a - b)
                .map(async (id) =>
                    walletBlockchains.push(await this.networksService.getBlockchainByChainId(id))
                );
        }
        return walletBlockchains;
    }
}

